import { useEffect, useMemo, useState } from 'react'
import PublicLayout from 'layouts/PublicLayout'
import { NextSeoProps } from 'next-seo'
import { useQuery } from 'urql'
import { useRouter } from 'next/router'
import placeholderImage from 'assets/images/placeholder.png'
import MagazineSection from 'components/sections/MagazineSection'
import dayjs from 'dayjs'
import { config } from 'data/config'
import PropertyGridDisplay from 'components/property/PropertyGridDisplay'
import { validateDate } from 'data/helpers/dates'
import GetInspiredSection from 'components/sections/GetInspiredSection'
import DestinationFiltersSection from 'components/sections/DestinationFiltersSection'
import { useInfiniteScroll } from 'data/hooks/useInfiniteScroll'
import { merge } from 'data/helpers/array'
import { useDestinationFetching } from 'data/hooks/Destinations/useDestinationFetching'
import { PropertiesOrderByClause } from 'gql/graphql'
import { useSearchParams } from 'data/hooks/useSearchParams'
import { MobileScreenWidths, useScreenWidth } from 'data/hooks/useScreenWidth'
import classNames from 'classnames'
import usePropertyFilters from 'data/hooks/usePropertyFilters'
import SearchModal from 'components/modals/home/search/SearchModal'
import { MobilePublicNavbarProps } from 'components/mobile/MobilePublicNavbar'
import { useMobileApp } from 'data/hooks/MobileApp/useMobileApp'
import { useHasScrolled } from 'data/hooks/useHasScrolled'
import PropertiesSortSelect from 'components/property/PropertiesSortSelect'
import { graphql } from 'gql'
import DestinationNavbar from 'components/home/navbar/destination/DestinationNavbar'

const ALL_PROPERTIES_QUERY = graphql(`
  query AllProperties(
    $priceDetails: PropertyPriceInput,
    $filters: PropertyFiltersInput,
    $page: Int!,
    $perPage: Int!,
    $orderBy: [PropertiesOrderByClause!]
  ) {
    properties(filters: $filters, page: $page, first: $perPage, orderBy: $orderBy) {
      data {
        # Start of PropertyCard_PropertyFragment
        id

        title
        reference
        url

        guests
        bedrooms
        bathrooms

        # Price
        price_range {
          min
          max
        }
        currency

        # Images
        main_image {
          id
          url
          file_name
        }
        main_image_alt_text

        images {
          id
          url
          file_name
        }

        # Location
        city {
          id
          value
        }
        region {
          id
          value
        }

        wishlists(first: 1) {
          paginatorInfo {
            total
          }
        }
        # End of PropertyCard_PropertyFragment
        stay_cost(details: $priceDetails)
      }
      paginatorInfo {
        hasMorePages
        currentPage
        count
      }
    }
  }
`)

const PAGE_QUERY = graphql(`
  query propertiesSearchPage {
    randomRegion {
      id
      main_media {
        id
        url
        file_name
      }
    }

    destinationFilters (parent_id: null, first: 100) {
      data {
        id
        value
        parent {
          id
          value
        }
      }
    }
  }
`)

const PublicSearchPage = () => {
  const router = useRouter()

  const { params } = useSearchParams()
  const { isMobile } = useScreenWidth(MobileScreenWidths.LG)
  const { isMobileApp } = useMobileApp()
  const { filters } = usePropertyFilters()
  const hasScrolled = useHasScrolled(60)

  const [properties, setProperties] = useState([])
  const [destinationFilters, setDestinationFilters] = useState<number[]>([])
  const [orderBy, setOrderBy] = useState<PropertiesOrderByClause[] | null>(null)
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false)

  const {
    infiniteScrollRef,
    page,
    setPage,
    data: propertyData,
    fetching,
  } = useInfiniteScroll(
    'properties.paginatorInfo',
    {
      query: ALL_PROPERTIES_QUERY,
      variables: {
        perPage: 6,
        slug: params.get('country'),
        filters: {
          ...filters,
          destination_filters: destinationFilters?.length > 0 ? destinationFilters : null,
        },
        ...(validateDate(params.get('arrival')) && validateDate(params.get('departure'))
          ? {
            priceDetails: {
              arrival: router.query.arrival,
              departure: router.query.departure,
            },
          }
          : null),
        orderBy: orderBy,
      },
    },
  )

  useEffect(() => {
    if (propertyData?.properties?.data) {
      if (propertyData?.properties?.paginatorInfo) {
        setProperties((propertyData?.properties.paginatorInfo.currentPage === 1)
          ? propertyData?.properties?.data
          : merge(properties, propertyData?.properties?.data),
        )
      } else {
        setProperties(propertyData?.properties?.data)
      }
    }
  }, [propertyData])

  useEffect(() => {
    if (destinationFilters?.length || orderBy || params) {
      setPage(1)
    }
  }, [destinationFilters?.length, orderBy, params])

  useDestinationFetching(fetching, page)

  const [{ data }] = useQuery({
    query: PAGE_QUERY,
  })

  const seo: NextSeoProps = {
    title: `Europe Holiday Villas & Chalets - Luxury Villa & Chalet Rental Europe - Europe Holiday Villa & Chalet Rentals ${dayjs().format('YYYY')}`,
    description: 'Luxury holiday villas & chalets to rent in Europe. Europe exclusive private villas & chalets. Book the best properties in Europe.',
    openGraph: {
      type: 'website',
      url: config.appURL + router.asPath,
      images: data?.randomRegion?.main_media?.url
        ? [{
          url: data?.randomRegion?.main_media?.url,
          width: 1800,
          height: 659,
          alt: data?.randomRegion?.main_media?.file_name,
        }]
        : [],
    },
  }

  const pageTitle = useMemo(
    () => params.size > 0 ? 'Search Results' : 'View All Luxury Villas',
    [params.size],
  )

  const mobileNavBar: MobilePublicNavbarProps = {
    visible: isMobileApp || (isMobile && hasScrolled),
    back: isMobileApp ?? false,
    profileTrigger: !isMobileApp,
    close: {
      enabled: isMobileApp ?? false,
      href: {
        pathname: '/home/search',
        query: router.query,
      },
    },
    className: {
      child: hasScrolled ? '!pb-[11.5px]' : '',
    },
    images: !isMobileApp
      ? [{
        url: data?.randomRegion?.main_media?.url ?? placeholderImage,
        alt_text: data?.randomRegion?.main_media?.file_name ?? '',
      }]
      : undefined,
    title: isMobileApp
      ? (
        hasScrolled
          ? <PropertiesSortSelect setOrderBy={setOrderBy} variant="narrow" />
          : pageTitle
      )
      : null,
  }

  return (
    <PublicLayout
      mobileNavBar={mobileNavBar}
      seo={seo}
      featuredImage={{
        image: data?.randomRegion?.main_media?.url ?? placeholderImage,
        size: 'md',
      }}
      header={{
        title: pageTitle,
      }}
      actionFooter={{
        button: {
          children: 'Edit Search',
          onClick: () => setIsSearchModalOpen(true),
        },
      }}
    >
      {
        isMobile
          ? null
          : <DestinationNavbar />
      }

      <div className="lg:mt-25 max-w-[1280px] mx-auto">
        <hr className="hidden mx-20 text-grey-200 mt-25 mb-50 lg:block" />

        <DestinationFiltersSection
          filters={data?.destinationFilters?.data ?? []}
          activeFilters={destinationFilters}
          setActiveFilters={setDestinationFilters}
          setOrderBy={setOrderBy}
        />
      </div>

      <div className={classNames('relative container-public mb-75', { 'mt-30': isMobile })}>
        <PropertyGridDisplay
          properties={properties}
          isLoading={fetching}
          target="_blank"
        />

        <div ref={infiniteScrollRef} className="h-[1px] w-full absolute bottom-[1000px] inset-x-0" />
      </div>

      <MagazineSection />

      <GetInspiredSection />

      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => {setIsSearchModalOpen(false)}}
      />
    </PublicLayout>
  )
}

export default PublicSearchPage
